import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import debounce from 'lodash/debounce';

import { Autocomplete, Box, Chip, CircularProgress, TextField, Typography } from '@mui/material';

import { PropertyResponse, listProperties } from '~/services/AccommodationService';

export interface PropertyOption {
  id: string;
  name: string;
}

interface Props {
  value: Array<PropertyOption>;
  onChange: (value: Array<PropertyOption>) => void;
  // Pre-attached property IDs for which we should resolve names from the server. Pass on mount only;
  // the picker hydrates names asynchronously and merges them into `value` via onChange.
  initialIds?: Array<string>;
  label?: string;
  placeholder?: string;
}

const SEARCH_LIMIT = 25;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Cap on parallel id-lookups during initial name hydration. Bundles in normal use have 1-5
// hotels; this is just a safety net for the rare big-bundle case.
const HYDRATION_CONCURRENCY = 6;

async function mapInChunks<T, U>(
  items: ReadonlyArray<T>,
  chunkSize: number,
  fn: (item: T) => Promise<U>,
): Promise<Array<U>> {
  const out: Array<U> = [];
  for (let i = 0; i < items.length; i += chunkSize) {
    const chunk = items.slice(i, i + chunkSize);
    const results = await Promise.all(chunk.map(fn));
    out.push(...results);
  }
  return out;
}

function BundlePropertyPicker({
  value,
  onChange,
  initialIds,
  label = 'Properties',
  placeholder = 'Search by name…',
}: Props) {
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Refs let the async hydration read the *latest* selection at write time. Closing over
  // `value` and `onChange` directly would let a slow lookup overwrite edits the user made
  // (re-adding removed hotels or dropping newly-added ones) once the resolved names land.
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Resolve names of pre-attached IDs once on mount. listProperties accepts a single `id`
  // filter, so we fan out one fetch per id and merge resolved names into the latest selection.
  useEffect(() => {
    if (!initialIds || initialIds.length === 0) return;
    let cancelled = false;
    mapInChunks(initialIds, HYDRATION_CONCURRENCY, (id) =>
      listProperties({ id, limit: '1', page: '1' })
        .then((res) => res.result.properties[0])
        .catch(() => undefined),
    ).then((results) => {
      if (cancelled) return;
      const resolved = results.filter((p): p is PropertyResponse => !!p);
      if (resolved.length === 0) return;
      // Merge into whatever's currently selected — preserves user edits made during the lookup.
      const current = valueRef.current;
      const next = current.map((opt) => {
        const match = resolved.find((p) => p.id === opt.id);
        return match ? { id: match.id, name: match.name } : opt;
      });
      // Skip the write if nothing actually changed (every removed-during-hydration id will fall
      // through to the `opt` branch unchanged), avoiding a needless render.
      const changed = next.some((opt, i) => opt !== current[i]);
      if (changed) onChangeRef.current(next);
    });
    return () => {
      cancelled = true;
    };
    // intentionally only on initialIds — name hydration is a one-shot side effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialIds]);

  const debounceSearch = useMemo(() => debounce((next: string) => setDebouncedSearch(next), 300), []);
  useEffect(() => () => debounceSearch.cancel(), [debounceSearch]);

  // Treat a full UUID as an id lookup; anything else is a name search. Lets admin paste an id in
  // and get a single hit, or type free text and get name matches.
  const isUuidSearch = UUID_RE.test(debouncedSearch.trim());

  const searchQuery = useQuery({
    queryKey: ['bundle-property-picker-search', debouncedSearch],
    queryFn: async () => {
      const trimmed = debouncedSearch.trim();
      if (isUuidSearch) {
        const res = await listProperties({ id: trimmed, limit: '1', page: '1' });
        return res.result.properties;
      }
      if (trimmed.length < 3) return [] as Array<PropertyResponse>;
      const res = await listProperties({ name: trimmed, limit: String(SEARCH_LIMIT), page: '1' });
      return res.result.properties;
    },
    enabled: isUuidSearch || debouncedSearch.trim().length >= 3,
    staleTime: 30_000,
  });

  const options: Array<PropertyOption> = useMemo(() => {
    const found = (searchQuery.data ?? []).map((p) => ({ id: p.id, name: p.name }));
    // Keep already-selected options visible even if the current search wouldn't return them.
    const merged = [...found];
    for (const opt of value) {
      if (!merged.some((m) => m.id === opt.id)) merged.push(opt);
    }
    return merged;
  }, [searchQuery.data, value]);

  return (
    <Autocomplete
      multiple
      options={options}
      value={value}
      onChange={(_event, next) => onChange(next)}
      inputValue={searchInput}
      onInputChange={(_event, next, reason) => {
        if (reason === 'reset') return;
        setSearchInput(next);
        debounceSearch(next);
      }}
      getOptionLabel={(opt) => opt.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(opts) => opts}
      loading={searchQuery.isFetching}
      noOptionsText={
        debouncedSearch.trim().length < 3 && !isUuidSearch
          ? 'Type at least 3 characters to search by name, or paste a property UUID'
          : searchQuery.isFetching
          ? 'Searching…'
          : 'No matching properties'
      }
      renderTags={(items, getTagProps) =>
        items.map((option, index) => (
          <Chip {...getTagProps({ index })} key={option.id} label={option.name} title={option.id} size="small" />
        ))
      }
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Box display="flex" flexDirection="column">
            <Typography variant="body2">{option.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {option.id}
            </Typography>
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length === 0 ? placeholder : ''}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {searchQuery.isFetching ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default BundlePropertyPicker;
